/**
 * Phase 5 — Battle worker WebSocket client (parallel/flagged).
 *
 * When the orchestrator publishes a `my_match_worker_endpoint` row for the
 * caller, and the `USE_WORKER_WS` feature flag is on, the client opens a
 * direct WebSocket to the per-match worker process and exchanges JSON
 * envelopes mirroring `wargame_worker_protocol::{ClientToWorker,
 * WorkerToClient}` (see `server-rs/crates/worker-protocol/src/lib.rs`).
 *
 * Renderer cutover (consuming `TickFrame` instead of SpacetimeDB row
 * subscriptions for `my_friendly_units` etc.) is intentionally left to a
 * follow-up so the existing data path keeps working while this scaffold
 * lands. For now the WS path mainly:
 *
 *   * authenticates with `ClientHello`,
 *   * surfaces `match_start` / `tick_frame` / `match_end` to callbacks,
 *   * provides `sendOrder()` so order reducer calls can be routed through
 *     the worker rather than `dispatchOrder` etc. when active.
 */

const PROTOCOL_VERSION = 1;

export interface WorkerHelloOpts {
  matchId: string;
  playerId: string;
  token: string;
  /** Last fully-rendered tick from a previous WS session, if reconnecting. */
  lastSeenTick?: number | null;
}

export interface WorkerTickFrame {
  tick: number;
  phase: string;
  full: boolean;
  units: any;
  enemy_units: any;
  unit_skills: any;
  unit_order_queue: any;
  unit_path_waypoints: any;
  engagements: any;
  projectiles: any;
  messengers: any;
  capture_points: any;
  structure_state: any;
  camps?: any;
  events: any;
  visibility: any;
  wall_build_projects?: any;
  roads?: any;
  wood_stockpile?: any;
  harvest_tiles?: any;
  ant_groups?: any;
  terrain_overrides?: any;
  watchtowers?: any;
}

export interface WorkerMatchEnd {
  match_id: string;
  winner: number | null;
  final_tick: number;
  summary: any;
  events: any;
}

export interface WorkerWsCallbacks {
  onOpen?: () => void;
  onMatchStart?: (info: { match_id: string; server_tick: number; tick_hz: number }) => void;
  onTickFrame?: (frame: WorkerTickFrame) => void;
  onMatchEnd?: (end: WorkerMatchEnd) => void;
  onError?: (code: string, message: string) => void;
  /** Fires once for any close (clean or error). `wasClean` mirrors WS event. */
  onClose?: (info: { code: number; reason: string; wasClean: boolean }) => void;
}

export class WorkerWsClient {
  private ws: WebSocket | null = null;
  private hello: WorkerHelloOpts;
  private cb: WorkerWsCallbacks;
  private url: string;
  private _open = false;
  private _closed = false;
  private _lastTick = 0;

  constructor(url: string, hello: WorkerHelloOpts, callbacks: WorkerWsCallbacks = {}) {
    this.url = url;
    this.hello = hello;
    this.cb = callbacks;
  }

  /** Latest tick received via TickFrame, or 0 if none yet. */
  get lastTick(): number {
    return this._lastTick;
  }

  get isOpen(): boolean {
    return this._open;
  }

  connect(): void {
    if (this.ws) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err: any) {
      console.warn("[worker-ws] WebSocket constructor threw:", err);
      this.cb.onError?.("connect_failed", String(err?.message || err));
      this.cb.onClose?.({ code: 1006, reason: "constructor failed", wasClean: false });
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this._open = true;
      const hello = {
        type: "hello",
        protocol_version: PROTOCOL_VERSION,
        match_id: this.hello.matchId,
        player_id: this.hello.playerId,
        token: this.hello.token,
        last_seen_tick:
          this.hello.lastSeenTick != null ? this.hello.lastSeenTick : null,
      };
      try {
        ws.send(JSON.stringify(hello));
      } catch (err) {
        console.warn("[worker-ws] failed to send hello:", err);
      }
      this.cb.onOpen?.();
    };

    ws.onmessage = (ev: MessageEvent) => {
      let msg: any;
      try {
        msg = JSON.parse(typeof ev.data === "string" ? ev.data : "");
      } catch (err) {
        console.warn("[worker-ws] non-JSON message dropped:", err);
        return;
      }
      this._dispatch(msg);
    };

    ws.onerror = (ev) => {
      // Browsers don't surface details. Just log; close handler runs after.
      console.warn("[worker-ws] socket error", ev);
    };

    ws.onclose = (ev) => {
      this._open = false;
      const wasClosed = this._closed;
      this._closed = true;
      this.ws = null;
      if (!wasClosed) {
        this.cb.onClose?.({
          code: ev.code,
          reason: ev.reason || "",
          wasClean: ev.wasClean,
        });
      }
    };
  }

  /** Send a single order envelope to the worker. Returns false if the WS is
   * not currently open (caller can fall back to a reducer). */
  sendOrder(action: string, payload: any): boolean {
    if (!this.ws || !this._open) return false;
    const msg = { type: "order", action, payload };
    try {
      this.ws.send(JSON.stringify(msg));
      return true;
    } catch (err) {
      console.warn("[worker-ws] sendOrder failed:", err);
      return false;
    }
  }

  /** Send a goodbye and close cleanly. */
  close(reason: string = "client_close"): void {
    if (this._closed) return;
    this._closed = true;
    if (this.ws && this._open) {
      try {
        this.ws.send(JSON.stringify({ type: "goodbye" }));
      } catch {
        /* best-effort */
      }
      try {
        this.ws.close(1000, reason);
      } catch {
        /* ignore */
      }
    }
    this._open = false;
    this.ws = null;
  }

  private _dispatch(msg: any): void {
    if (!msg || typeof msg.type !== "string") return;
    switch (msg.type) {
      case "match_start":
        this.cb.onMatchStart?.({
          match_id: msg.match_id,
          server_tick: Number(msg.server_tick) || 0,
          tick_hz: Number(msg.tick_hz) || 0,
        });
        break;
      case "tick_frame": {
        const frame: WorkerTickFrame = {
          tick: Number(msg.tick) || 0,
          phase: String(msg.phase || ""),
          full: !!msg.full,
          units: msg.units,
          enemy_units: msg.enemy_units,
          unit_skills: msg.unit_skills,
          unit_order_queue: msg.unit_order_queue,
          unit_path_waypoints: msg.unit_path_waypoints,
          engagements: msg.engagements,
          projectiles: msg.projectiles,
          messengers: msg.messengers,
          capture_points: msg.capture_points,
          structure_state: msg.structure_state,
          camps: msg.camps,
          events: msg.events,
          visibility: msg.visibility,
          wall_build_projects: msg.wall_build_projects,
          wood_stockpile: msg.wood_stockpile,
          harvest_tiles: msg.harvest_tiles,
          ant_groups: msg.ant_groups,
          terrain_overrides: msg.terrain_overrides,
          watchtowers: msg.watchtowers,
          roads: msg.roads,
        };
        if (frame.tick > this._lastTick) this._lastTick = frame.tick;
        this.cb.onTickFrame?.(frame);
        break;
      }
      case "match_end":
        this.cb.onMatchEnd?.({
          match_id: msg.match_id,
          winner: msg.winner ?? null,
          final_tick: Number(msg.final_tick) || 0,
          summary: msg.summary,
          events: msg.events,
        });
        break;
      case "pong":
        // Not used yet.
        break;
      case "error":
        this.cb.onError?.(String(msg.code || ""), String(msg.message || ""));
        break;
      default:
        // Forward-compat: ignore unknown types.
        break;
    }
  }
}

/** Reads the runtime feature flag for the parallel worker-WS path.
 *  Default ON (the worker WebSocket is now the primary sim data path).
 *  Disable by setting `window.WARGAME_USE_WORKER_WS = false` before the
 *  bundle runs, or `localStorage.USE_WORKER_WS = "0"` in DevTools. */
export function isWorkerWsEnabled(): boolean {
  try {
    if ((window as any).WARGAME_USE_WORKER_WS === false) return false;
    if (typeof localStorage !== "undefined" &&
        localStorage.getItem("USE_WORKER_WS") === "0") {
      return false;
    }
  } catch {
    /* SSR / sandbox without window — treat as on */
  }
  return true;
}
